import { supabase } from '../../../lib/supabase';
import { fetchMessageHistoryText } from '../aiChatContext';
import { callGeminiProxy, stripCodeFences } from './geminiProxy';

const generateLeadSummary = async (
    leadDataInfo: string,
    chatHistoryText: string,
    tasksText: string
): Promise<string> => {
    const prompt = `
📋 PAPEL: Você é um Assistente de Inteligência Artificial de Vendas, projetado para ler históricos de negociações, conversas do chat e tarefas e gerar um resumo executivo limpo.

DADOS DA NEGOCIAÇÃO:
${leadDataInfo || 'Nenhum dado principal.'}

HISTÓRICO DA CONVERSA (CHAT):
${chatHistoryText || 'Nenhuma conversa registrada.'}

TAREFAS ASSOCIADAS:
${tasksText || 'Nenhuma tarefa.'}

OBJETIVO OBRIGATÓRIO: 
Analise minuciosamente todo o histórico da conversa e as tarefas. Preste atenção na evolução da intenção do cliente (exemplo: se ele entrou por um motivo, mas acabou se interessando por outro produto). Capture valores ocultos, mudanças de humor e necessidades de cross-sell.
Retorne ESTRITAMENTE o resumo utilizando o seguinte formato HTML básico. Responda apenas com este bloco HTML, sem introduções ou blocos de código \`\`\`html. Seja incisivo, analítico e não deixe passar o contexto vital da venda. Toda sua saída será injetada em um editor visual, portanto apenas gere as TAGs.

<p><strong>✨ Resumo Estratégico (IA)</strong></p>
<p><strong>Perfil:</strong> [Diagnóstico do cliente: quem é, o que inicialmente buscava, nível de abertura ou objeção atual]</p>
<p><strong>Produto/Interesse Real:</strong> [O que foi ofertado versus o que ele de fato demonstrou interesse no final da conversa]</p>
<p><strong>Situação atual:</strong> [Resumo do status atual da negociação, onde a conversa parou ou qual foi a objeção final]</p>
<p><strong>Temperatura:</strong> [MUITO QUENTE, QUENTE, MORNO ou FRIO - justifique em poucas palavras com base na interação]</p>
<p><strong>Próximo passo:</strong> [Diretriz exata do que o atendente/vendedor DEVE fazer em seguida para avançar a venda ou basear-se nas tarefas pendentes]</p>
<p><strong>Retornar em:</strong> [Data ou hora exata baseada nas promessas do chat ou tarefas - se houver uma tarefa para amanhã, diga explicitamente a data. Caso contrário, analise quando seria o follow-up ideal]</p>
    `;

    const rawText = await callGeminiProxy(prompt, { temperature: 0.2 });
    return stripCodeFences(rawText);
};

async function fetchLeadTasksText(leadId: string): Promise<string> {
    const { data: tasks } = await supabase.from('tasks').select('*').eq('lead_id', leadId);
    if (!tasks?.length) return 'Nenhuma tarefa associada encontrada.';

    return tasks
        .map((t: { status?: string; title?: string; due_date?: string }) =>
            `- [${t.status === 'completed' ? 'x' : ' '}] ${t.title} ${t.due_date ? `(Para ${t.due_date})` : ''}`
        )
        .join('\n');
}

function formatLeadInfoForSummary(leadData: {
    name?: string;
    origin?: string;
    value?: number | string;
}): string {
    return `
Nome: ${leadData.name || 'Desconhecido'}
Canal: ${leadData.origin || 'N/A'}
Valor: R$ ${leadData.value || '0.00'}
    `.trim();
}

export const generateSummaryForLead = async (leadData: {
    id: string;
    name?: string;
    origin?: string;
    value?: number | string;
    phone?: string;
    whatsapp?: string;
} | null): Promise<string> => {
    try {
        if (!leadData) {
            throw new Error('Falha ao encontrar negociação associada.');
        }

        const [tasksText, chatHistoryText] = await Promise.all([
            fetchLeadTasksText(leadData.id),
            fetchMessageHistoryText({
                phone: leadData.phone,
                whatsapp: leadData.whatsapp,
            }),
        ]);

        return await generateLeadSummary(
            formatLeadInfoForSummary(leadData),
            chatHistoryText,
            tasksText
        );
    } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Erro desconhecido ao gerar resumo.';
        console.error('Erro interno ao gerar resumo inteligente:', message);
        throw new Error(message);
    }
};
